import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, FileText, CheckCircle2, MessageCircle, BadgeCheck } from "lucide-react";
import { TRACK_STATUS, statusLabel, useLang, DIRECTION_LABEL } from "@/i18n";
import { api, apiError, API } from "@/lib/api";
import { fmtGBP, fmtIDR, fmtDateTime, gbpToIdr } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";

const toLocalInput = (iso) => (iso ? iso.slice(0, 16) : "");

export const BookingDialog = ({ open, onOpenChange, booking, onSaved }) => {
  const { lang } = useLang();
  const [b, setB] = useState(booking);
  const [saving, setSaving] = useState(false);
  const [track, setTrack] = useState({ status: "goods_received", note: "", date: "" });

  useEffect(() => { setB(booking); }, [booking]);
  if (!b) return null;

  const trip = b.trip || {};
  const rate = trip.exchange_rate_gbp_to_idr;
  const set = (k, v) => setB((p) => ({ ...p, [k]: v }));
  const setCustoms = (i, k, v) => setB((p) => ({ ...p, customs_charges: p.customs_charges.map((c, idx) => idx === i ? { ...c, [k]: v } : c) }));
  const hasAdjusted = b.adjusted_weight_kg != null && `${b.adjusted_weight_kg}` !== "";
  const canComplete = b.status === "delivered" && b.payment_status === "paid";

  const buildPayload = () => ({
    adjusted_weight_kg: b.adjusted_weight_kg === "" || b.adjusted_weight_kg == null ? null : Number(b.adjusted_weight_kg),
    bulky_quote_gbp: b.bulky_quote_gbp === "" || b.bulky_quote_gbp == null ? null : Number(b.bulky_quote_gbp),
    customs_charges: (b.customs_charges || []).filter((c) => c.description).map((c) => ({ description: c.description, amount_idr: Number(c.amount_idr) || 0 })),
    delivery_option: b.delivery_option,
    delivery_payer: b.delivery_payer,
    recipient_address: b.recipient_address,
    domestic_cost_idr: b.domestic_cost_idr === "" || b.domestic_cost_idr == null ? null : Number(b.domestic_cost_idr),
    domestic_awb: b.domestic_awb,
    pickup_ready_datetime: b.pickup_ready_datetime || null,
    payment_status: b.payment_status,
  });
  const persist = async () => {
    const { data } = await api.put(`/admin/bookings/${b.reference}`, buildPayload());
    return data;
  };

  const saveDetails = async () => {
    setSaving(true);
    try {
      const data = await persist();
      setB({ ...data, trip });
      toast.success("Booking updated");
      onSaved();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const addTracking = async () => {
    try {
      await persist();
      const { data } = await api.post(`/admin/bookings/${b.reference}/tracking`, {
        status: track.status, note: track.note || null,
        date: track.date ? (track.date.length === 16 ? track.date + ":00" : track.date) : null,
      });
      setB({ ...data, trip });
      setTrack({ status: "goods_received", note: "", date: "" });
      toast.success("Tracking updated");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const complete = async () => {
    try {
      await persist();
      const { data } = await api.post(`/admin/bookings/${b.reference}/complete`);
      setB({ ...data, trip });
      toast.success("Booking marked completed");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const confirmBooking = async () => {
    try { await persist(); const { data } = await api.post(`/admin/bookings/${b.reference}/confirm`); setB({ ...data, trip }); toast.success("Booking confirmed"); onSaved(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const verifyPayment = async () => {    try {
      const { data } = await api.put(`/admin/bookings/${b.reference}`, { payment_status: "paid" });
      setB({ ...data, trip });
      toast.success("Payment verified");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const waNotify = () => {
    const digits = (b.whatsapp || "").replace(/\D/g, "");
    const intl = digits.startsWith("0") ? "62" + digits.slice(1) : digits.replace(/^\+/, "");
    const last = (b.tracking || [])[b.tracking.length - 1];
    const st = last ? statusLabel(TRACK_STATUS, last.status, lang) : "";
    const msg = `Halo ${b.customer_name}, update titipan ${b.reference}: "${st}". Lacak: ${window.location.origin}/track?ref=${b.reference}`;
    window.open(`https://wa.me/${intl}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="booking-dialog" className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-3 flex-wrap">
            <span className="font-mono">{b.reference}</span>
            {b.flagged && <Badge variant="destructive" data-testid="flagged-badge">Over capacity — review</Badge>}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 text-sm">
          {/* Customer + items */}
          <section>
            <p className="font-semibold text-base">{b.customer_name}</p>
            <p className="text-muted-foreground">{trip.title} · {DIRECTION_LABEL[trip.direction]}</p>
            <p className="text-muted-foreground">Phone: {b.phone} · WA: {b.whatsapp}</p>
          </section>

          <section className="rounded-lg border border-border p-3 space-y-2">
            <div className="font-semibold">Goods {b.is_bulky && <Badge className="ml-2">Bulky</Badge>}</div>
            {(b.items || []).map((it, i) => (
              <div key={i} className="flex items-center justify-between gap-2">
                <span>{it.name} × {it.quantity} · {fmtGBP(it.price_value)}</span>
                {it.receipt_file_path && (
                  <a href={`${API}/files/${it.receipt_file_path}`} target="_blank" rel="noreferrer" className="text-primary flex items-center gap-1 text-xs" data-testid={`receipt-link-${i}`}>
                    <FileText className="h-3.5 w-3.5" /> {it.receipt_filename || "receipt"}
                  </a>
                )}
              </div>
            ))}
          </section>

          {/* Weights & fees */}
          <section className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Customer estimated weight (kg)</Label>
              <Input value={b.estimated_weight_kg} disabled />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Adjusted weight after reweigh (kg)</Label>
              <Input data-testid="adjusted-weight-input" type="number" step="0.1" value={b.adjusted_weight_kg ?? ""} onChange={(e) => set("adjusted_weight_kg", e.target.value)} />
            </div>
            {b.is_bulky && trip.bulky_pricing_type === "quote" && (
              <div className="space-y-1.5">
                <Label className="text-xs">Bulky manual quote (GBP)</Label>
                <Input data-testid="bulky-quote-input" type="number" step="0.01" value={b.bulky_quote_gbp ?? ""} onChange={(e) => set("bulky_quote_gbp", e.target.value)} />
              </div>
            )}
          </section>
          <div className="flex gap-6 text-sm">
            <span>Est. fee: <b>{fmtGBP(b.estimated_fee_gbp)}</b> {rate && <span className="text-muted-foreground font-mono">≈ {fmtIDR(gbpToIdr(b.estimated_fee_gbp, rate))}</span>}</span>
            {b.final_fee_gbp != null && <span>Final fee: <b className="text-primary">{fmtGBP(b.final_fee_gbp)}</b> {rate && <span className="text-muted-foreground font-mono">≈ {fmtIDR(gbpToIdr(b.final_fee_gbp, rate))}</span>}</span>}
          </div>

          {/* Customs */}
          <section className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Customs duties / taxes (IDR)</Label>
              <Button variant="outline" size="sm" data-testid="add-customs-btn" onClick={() => set("customs_charges", [...(b.customs_charges || []), { description: "", amount_idr: 0 }])}>
                <Plus className="h-3.5 w-3.5 mr-1" /> Add
              </Button>
            </div>
            {(b.customs_charges || []).map((c, i) => (
              <div key={i} className="flex gap-2">
                <Input placeholder="Description" data-testid={`customs-desc-${i}`} value={c.description} onChange={(e) => setCustoms(i, "description", e.target.value)} />
                <Input placeholder="IDR" type="number" className="w-40" data-testid={`customs-amount-${i}`} value={c.amount_idr} onChange={(e) => setCustoms(i, "amount_idr", e.target.value)} />
                <Button variant="ghost" size="icon" onClick={() => set("customs_charges", b.customs_charges.filter((_, idx) => idx !== i))}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </section>

          {/* Delivery */}
          <section className="space-y-3">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs">Delivery option</Label>
                <Select value={b.delivery_option} onValueChange={(v) => set("delivery_option", v)}>
                  <SelectTrigger data-testid="delivery-option-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pickup">Self pick-up</SelectItem>
                    <SelectItem value="delivery">Domestic delivery</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Payment status</Label>
                <Select value={b.payment_status} onValueChange={(v) => set("payment_status", v)}>
                  <SelectTrigger data-testid="payment-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unpaid">Unpaid</SelectItem>
                    <SelectItem value="awaiting_verification">Awaiting verification</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {b.delivery_option === "delivery" ? (
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-xs">Recipient address</Label>
                  <Textarea data-testid="recipient-address-input" value={b.recipient_address || ""} onChange={(e) => set("recipient_address", e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Who pays domestic cost</Label>
                  <Select value={b.delivery_payer || "customer"} onValueChange={(v) => set("delivery_payer", v)}>
                    <SelectTrigger data-testid="payer-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="customer">Customer</SelectItem>
                      <SelectItem value="recipient">Recipient</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Domestic cost (IDR)</Label>
                  <Input data-testid="domestic-cost-input" type="number" value={b.domestic_cost_idr ?? ""} onChange={(e) => set("domestic_cost_idr", e.target.value)} />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-xs">Domestic AWB / tracking no.</Label>
                  <Input data-testid="awb-input" value={b.domestic_awb || ""} onChange={(e) => set("domestic_awb", e.target.value)} />
                </div>
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label className="text-xs">Ready for pick-up date/time</Label>
                <Input data-testid="pickup-datetime-input" type="datetime-local" value={toLocalInput(b.pickup_ready_datetime)} onChange={(e) => set("pickup_ready_datetime", e.target.value)} />
              </div>
            )}
          </section>

          <div className="flex gap-2 flex-wrap">
            <Button data-testid="save-booking-btn" onClick={saveDetails} disabled={saving}>{saving ? "Saving..." : "Save details"}</Button>
            {b.status === "waiting_list" && (
              <Button data-testid="confirm-booking-btn" onClick={confirmBooking} disabled={!hasAdjusted}><BadgeCheck className="h-4 w-4 mr-1" /> Confirm booking</Button>
            )}
            {b.payment_status === "awaiting_verification" && (
              <Button variant="outline" data-testid="verify-payment-btn" onClick={verifyPayment}><BadgeCheck className="h-4 w-4 mr-1" /> Verify payment</Button>
            )}
            {b.status !== "completed" && (
              <Button variant="outline" data-testid="complete-booking-btn" onClick={complete} disabled={!canComplete}><CheckCircle2 className="h-4 w-4 mr-1" /> Mark completed</Button>
            )}
          </div>
          {!hasAdjusted && b.status !== "completed" && (
            <p className="text-xs text-amber-600 dark:text-amber-400" data-testid="adj-required-note">Enter and save the adjusted (reweighed) weight to confirm the booking or change the shipment status.</p>
          )}
          {b.status !== "completed" && !canComplete && (
            <p className="text-xs text-muted-foreground" data-testid="complete-req-note">To mark completed: shipment status must be "Delivered" and payment must be "Paid".</p>
          )}

          <Separator />

          {/* Tracking */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="font-semibold">Tracking updates</Label>
              <Button variant="outline" size="sm" data-testid="wa-notify-btn" onClick={waNotify}>
                <MessageCircle className="h-3.5 w-3.5 mr-1 text-emerald-500" /> WhatsApp update to customer
              </Button>
            </div>
            <div className="space-y-1.5 max-h-40 overflow-y-auto">
              {(b.tracking || []).map((e, i) => (
                <div key={i} className="flex items-center justify-between text-xs border-b border-border pb-1">
                  <span className="font-medium">{statusLabel(TRACK_STATUS, e.status, lang)}{e.note ? ` — ${e.note}` : ""}</span>
                  <span className="text-muted-foreground font-mono">{fmtDateTime(e.date, lang)}</span>
                </div>
              ))}
            </div>
            <div className="grid sm:grid-cols-4 gap-2 items-end">
              <div className="sm:col-span-1">
                <Label className="text-xs">Status</Label>
                <Select value={track.status} onValueChange={(v) => setTrack((p) => ({ ...p, status: v }))}>
                  <SelectTrigger data-testid="tracking-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.keys(TRACK_STATUS).map((s) => <SelectItem key={s} value={s}>{statusLabel(TRACK_STATUS, s, lang)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-1">
                <Label className="text-xs">Date (optional)</Label>
                <Input type="datetime-local" data-testid="tracking-date-input" value={track.date} onChange={(e) => setTrack((p) => ({ ...p, date: e.target.value }))} />
              </div>
              <div className="sm:col-span-1">
                <Label className="text-xs">Note</Label>
                <Input data-testid="tracking-note-input" value={track.note} onChange={(e) => setTrack((p) => ({ ...p, note: e.target.value }))} />
              </div>
              <Button data-testid="add-tracking-btn" onClick={addTracking} disabled={!hasAdjusted}>Add</Button>
            </div>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
};
